import React from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../LayoutWrapper/AdminLayout";
import { useTranslation } from "react-i18next";
import profile from "../../Images/About/profile.jpg";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import "../../CSS/About/modern.css";

const VALUES = [
  {
    icon: TuneOutlinedIcon,
    title: "Customization",
    text: "We believe a one-size-fits-all approach doesn't cut it. Every aspect of your trip aligns with your vision.",
  },
  {
    icon: PublicOutlinedIcon,
    title: "Authenticity",
    text: "We take you beyond tourist traps to experience local culture, cuisine, and traditions.",
  },
  {
    icon: SpaOutlinedIcon,
    title: "Well-being",
    text: "We prioritize holistic relaxation through tailored activities and mindful experiences.",
  },
  {
    icon: EnergySavingsLeafOutlinedIcon,
    title: "Sustainability",
    text: "We partner with eco-conscious providers and communities that benefit from your visit.",
  },
];

const CONTENT_SECTIONS = [
  {
    title: "Your Gateway to Relaxation and Rejuvenation at Comfort Trip",
    paragraphs: [
      "Welcome to Comfort Trip, your one-stop shop for crafting unforgettable getaways designed for pure relaxation and rejuvenation. We're a passionate team of travel experts dedicated to curating bespoke experiences that cater to your specific needs and desires. Whether you crave a secluded beach escape, a charming village exploration, or an invigorating adventure, we'll help you unwind and reconnect with yourself amidst breathtaking landscapes and unique cultures.",
    ],
  },
  {
    title: "Our Story: A Passion for Crafted Comfort",
    paragraphs: [
      "Comfort Trip wasn't born in a corporate boardroom. It emerged from a shared desire – a yearning for travel experiences that transcended the typical tourist itinerary. We, the founders, were seasoned travelers ourselves, but we often felt a disconnect between the advertised packages and the actual experience. We craved personalized journeys that fostered genuine relaxation and allowed us to truly immerse ourselves in the beauty of a destination. This gap in the travel industry sparked the creation of Comfort Trip.",
    ],
  },
  {
    title: "Our Mission: Curating Personalized Journeys for Your Well-being",
    paragraphs: [
      "We believe travel should be a source of well-being, a chance to escape the daily grind and return feeling refreshed and revitalized. That's why we go beyond just booking flights and hotels. We take the time to understand you, your travel preferences, and your deepest relaxation goals. It's through this personalized approach that we craft journeys that cater to your individual needs and desires.",
    ],
  },
  {
    title: "Our Core Values: Guiding Principles for Your Comfort",
    paragraphs: [
      "At Comfort Trip, four core values guide every decision we make and every experience we curate:",
      "Customization: We believe a one-size-fits-all approach doesn't cut it. We work closely with you to understand your travel personality, interests, and relaxation needs.",
      "Authenticity: We take you beyond the tourist traps and immerse you in the true essence of a destination.",
      "Well-being: We prioritize your well-being throughout the journey, ensuring your trip fosters holistic relaxation and rejuvenation.",
      "Sustainability: We partner with eco-conscious hotels, activities that respect the environment, and local communities that benefit from your visit.",
    ],
  },
  {
    title: "Our Team: Your Travel Experts and Comfort Champions",
    paragraphs: [
      "Our team is at the heart of Comfort Trip. We're a diverse group of passionate travel enthusiasts, each with a wealth of experience and a deep understanding of different cultures and destinations. We're not just travel agents; we're your dedicated advisors, always on hand to answer your questions, address your concerns, and ensure your comfort throughout the journey.",
    ],
  },
  {
    title: "Our Commitment to You: Unforgettable Memories and Lasting Relaxation",
    paragraphs: [
      "At Comfort Trip, we believe in the transformative power of travel. Our commitment is simple: to craft unforgettable getaways that allow you to disconnect from your daily life, reconnect with yourself, and return home feeling revitalized and ready to face the world. We're more than just a travel company; we're your partners in creating lasting memories and fostering your well-being through the joy of travel.",
    ],
  },
  {
    title: "Ready to Start Your Comfort Journey?",
    paragraphs: [
      "We invite you to reach out to our friendly team today. Let's discuss your ideal escape, your relaxation goals, and the destinations that call to your heart. We'll listen attentively, share our expertise, and curate a personalized itinerary that promises unforgettable comfort and rejuvenation.",
      "Together, let's turn your travel dreams into a reality at Comfort Trip.",
    ],
  },
];

const AboutUs = () => {
  const { t } = useTranslation();

  return (
    <AdminLayout>
      <div className="about-page">
        <section className="about-hero">
          <div className="about-hero__inner">
            <span className="about-hero__badge">
              <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
              Comfort Trip
            </span>
            <h1 className="about-hero__title">{t("page_title_about")}</h1>
            <p className="about-hero__subtitle">{t("about_section_para1")}</p>
          </div>
        </section>

        <section className="about-intro">
          <div className="about-intro__inner">
            <div className="about-intro__image-wrap">
              <img
                src={profile}
                alt="Comfort Trip team"
                className="about-intro__image"
              />
              <span className="about-intro__image-accent" aria-hidden="true" />
            </div>
            <div className="about-intro__content">
              <p className="about-intro__label">Who we are</p>
              <h2 className="about-intro__title">{t("about_section_title1")}</h2>
              <p className="about-intro__text">{t("about_section_para1")}</p>
            </div>
          </div>
        </section>

        <section className="about-values">
          <div className="about-values__inner">
            <header className="about-values__header">
              <h2 className="about-values__title">What we stand for</h2>
              <p className="about-values__subtitle">
                Four core values guide every decision we make and every experience we curate.
              </p>
            </header>
            <div className="about-values__grid">
              {VALUES.map((value) => {
                const Icon = value.icon;
                return (
                  <article key={value.title} className="about-value-card">
                    <div className="about-value-card__icon">
                      <Icon />
                    </div>
                    <h3 className="about-value-card__title">{value.title}</h3>
                    <p className="about-value-card__text">{value.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="about-content">
          <div className="about-content__inner">
            {CONTENT_SECTIONS.map((section) => (
              <article key={section.title} className="about-section-block">
                <h3 className="about-section-block__title">{section.title}</h3>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 40)} className="about-section-block__text">
                    {paragraph}
                  </p>
                ))}
              </article>
            ))}
          </div>
        </section>

        <section className="about-cta">
          <div className="about-cta__inner">
            <h2 className="about-cta__title">Ready to experience Comfort Trip?</h2>
            <p className="about-cta__text">
              Book a premium ride or join our network of professional drivers today.
            </p>
            <div className="about-cta__actions">
              <Link to="/user-signup" className="about-cta__btn about-cta__btn--primary">
                <FavoriteBorderOutlinedIcon fontSize="small" />
                Book a ride
                <ArrowForwardIcon fontSize="small" />
              </Link>
              <Link to="/driver-signup" className="about-cta__btn about-cta__btn--outline">
                <GroupsOutlinedIcon fontSize="small" />
                Become a driver
              </Link>
              <Link to="/contact" className="about-cta__btn about-cta__btn--outline">
                Contact us
              </Link>
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AboutUs;
